import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Grid, LayoutTemplate, Plus, Sparkles } from 'lucide-react';
import WorkEditModal from '../work/WorkEditModal';
import ServiceEditModal from '../service/ServiceEditModal';
import BriefEditModal from '../brief/BriefEditModal';
import { postService, type WorkSubmitMode } from '../../../services/postService';
import { useToast } from '../../UI/Toast';

const BACKGROUND_WORK_UPLOAD_COMPLETED_EVENT = 'background_work_upload_completed';
const BACKGROUND_WORK_UPLOAD_FAILED_EVENT = 'background_work_upload_failed';

type PublisherCreationType = 'work' | 'service' | 'brief';

interface PublisherUserInfo {
    id: string;
    name: string;
    avatar: string;
}

interface PublisherProps {
    isLoggedIn: boolean;
    userInfo?: PublisherUserInfo;
    onRequireAuth: () => void;
}

const Publisher: React.FC<PublisherProps> = ({ isLoggedIn, userInfo, onRequireAuth }) => {
    const [creationType, setCreationType] = useState<PublisherCreationType | null>(null);
    const { showToast } = useToast();

    useEffect(() => {
        const handleBackgroundUploadCompleted = (event: Event) => {
            const detail = (event as CustomEvent<{
                submitMode?: WorkSubmitMode;
                work?: { auditStatus?: string; rejectionReason?: string };
            }>).detail;
            if (detail?.work?.auditStatus === 'REJECTED') {
                showToast(detail.work.rejectionReason || '作品审核未通过，可在作品卡片中查看原因并申诉', 'error');
            } else {
                showToast(detail?.submitMode === 'draft' ? '草稿素材上传完成' : '作品素材上传完成', 'success');
            }
            // Clear draft here (not inside modal) because modal unmounts on close
            try { sessionStorage.removeItem('publish_work_draft'); } catch {}
        };

        const handleBackgroundUploadFailed = (event: Event) => {
            const detail = (event as CustomEvent<{ submitMode?: WorkSubmitMode; error?: string }>).detail;
            showToast(detail?.error || (detail?.submitMode === 'draft' ? '草稿保存失败' : '作品发布失败'), 'error');
            // Re-open the work modal so the user can retry; WorkEditModal will restore from sessionStorage
            setCreationType('work');
        };

        window.addEventListener(BACKGROUND_WORK_UPLOAD_COMPLETED_EVENT, handleBackgroundUploadCompleted);
        window.addEventListener(BACKGROUND_WORK_UPLOAD_FAILED_EVENT, handleBackgroundUploadFailed);

        return () => {
            window.removeEventListener(BACKGROUND_WORK_UPLOAD_COMPLETED_EVENT, handleBackgroundUploadCompleted);
            window.removeEventListener(BACKGROUND_WORK_UPLOAD_FAILED_EVENT, handleBackgroundUploadFailed);
        };
    }, [showToast]);

    const handlePublishClick = () => {
        if (!isLoggedIn) {
            onRequireAuth();
            return;
        }
    };

    const handleSelectCreationType = (type: PublisherCreationType) => {
        if (!isLoggedIn) {
            onRequireAuth();
            return;
        }
        setCreationType(type);
    };

    const handlePublishWork = async (formData: any, submitMode: WorkSubmitMode, currentWork?: any) => {
        if (currentWork?.id) {
            const response = await postService.updateWork(String(currentWork.id), formData, submitMode, {
                background: true,
                creator: userInfo,
                existingWork: {
                    id: String(currentWork.id),
                    creatorUserId: userInfo?.id || '',
                    type: currentWork.type,
                    videoUrl: currentWork.videoUrl,
                    images: currentWork.images,
                    thumbnailUrl: currentWork.thumbnailUrl,
                    title: currentWork.title,
                    description: currentWork.description,
                    tags: currentWork.tags,
                    categories: currentWork.categories,
                    industry: currentWork.industry,
                    collaborators: currentWork.collaborators,
                    visibility: currentWork.visibility,
                    status: currentWork.status,
                    createdAt: currentWork.createdAt
                }
            });

            if (!response.success) {
                const errorMessage = response.error || (submitMode === 'draft' ? '草稿保存失败' : '作品发布失败');
                showToast(errorMessage, 'error');
                throw new Error(errorMessage);
            }

            showToast(submitMode === 'draft' ? '草稿已加入后台保存' : '作品已加入后台发布', 'success');
            if (submitMode === 'publish') {
                setCreationType(null);
            }

            return { postId: currentWork.id };
        }

        const result = await postService.publishWork(formData, undefined, submitMode, {
            background: true,
            creator: userInfo
        });

        if (!result.success) {
            const errorMessage = ('error' in result && result.error)
                ? result.error
                : (submitMode === 'draft' ? '草稿保存失败' : '作品发布失败');
            showToast(errorMessage, 'error');
            throw new Error(errorMessage);
        }

        showToast(submitMode === 'draft' ? '草稿已加入后台保存' : '作品已加入后台发布', 'success');
        if (submitMode === 'publish') {
            setCreationType(null);
        }

        return { postId: result.data?.postId };
    };

    const handlePublishService = (serviceData: any) => {
        if (serviceData?.status !== 'draft') {
            showToast('服务已发布', 'success');
        }
        setCreationType(null);
    };

    const handlePublishBrief = (briefData: any) => {
        if (briefData?.status !== 'draft') {
            showToast('需求已发布', 'success');
        }
        setCreationType(null);
    };

    const modalLayer = (
        <>
            <WorkEditModal
                isOpen={creationType === 'work'}
                onClose={() => setCreationType(null)}
                onSave={handlePublishWork}
                zIndex={80}
            />

            <ServiceEditModal
                isOpen={creationType === 'service'}
                onClose={() => setCreationType(null)}
                onSave={handlePublishService}
                isOwnProfile={true}
                userInfo={userInfo}
            />

            <BriefEditModal
                isOpen={creationType === 'brief'}
                onClose={() => setCreationType(null)}
                onSave={handlePublishBrief}
                isOwnProfile={true}
                userInfo={userInfo}
            />
        </>
    );

    return (
        <div className="group/publisher relative" data-tour="publish">
            <button
                type="button"
                onClick={handlePublishClick}
                className="relative flex h-11 items-center justify-center gap-2 overflow-hidden rounded-full border border-white/40 bg-[linear-gradient(135deg,oklch(0.62_0.21_258),oklch(0.55_0.22_294))] px-4 text-base font-semibold text-white shadow-[0_12px_28px_oklch(0.52_0.18_270_/_0.28)] transition-[transform,box-shadow,filter] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_18px_36px_oklch(0.52_0.18_270_/_0.36)] hover:brightness-105 active:translate-y-0 active:scale-[0.98] sm:px-5"
            >
                <span className="pointer-events-none absolute inset-0 rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.28),rgba(255,255,255,0)_48%,rgba(0,0,0,0.12))]" />
                <span className="pointer-events-none absolute inset-y-0 -left-12 w-10 rotate-12 bg-white/[0.35] blur-sm transition-transform duration-500 ease-out group-hover/publisher:translate-x-36" />
                <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-white/[0.16] shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]">
                    <Plus className="h-[18px] w-[18px] transition-transform duration-200 ease-out group-hover/publisher:rotate-90" strokeWidth={2.8} />
                </span>
                <span className="relative hidden tracking-[0.01em] sm:inline">发布</span>
            </button>

            {/* Hover Menu */}
            <div className="invisible absolute right-1/2 top-full z-[60] translate-x-1/2 translate-y-1 pt-4 opacity-0 transition-all duration-200 ease-out group-hover/publisher:visible group-hover/publisher:translate-y-0 group-hover/publisher:opacity-100">
                <div className="w-40 overflow-hidden rounded-2xl border border-gray-100 bg-white py-2 shadow-[0_18px_45px_rgba(15,23,42,0.12)]">
                    <button
                        onClick={() => handleSelectCreationType('work')}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    >
                        <Grid className="w-4 h-4 text-blue-600" />
                        发布作品
                    </button>
                    <button
                        onClick={() => handleSelectCreationType('service')}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    >
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        发布服务
                    </button>
                    <button
                        onClick={() => handleSelectCreationType('brief')}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    >
                        <LayoutTemplate className="w-4 h-4 text-green-600" />
                        发布需求
                    </button>
                </div>
            </div>

            {typeof document !== 'undefined' ? createPortal(modalLayer, document.body) : null}
        </div>
    );
};

export default Publisher;
